import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "react-select";
import DashboardLayout from "../../common/DashboardLayout";
import CertificationServices, { saveBlob, readBlobError } from "../../../services/CertificationServices";
import { GROUP_DEFINITIONS, getGroupForCertification } from "../../../config/certificationGroups";

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const formatDateDisplay = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const statusLabels = {
  pending_review: "Pending Review",
  approved: "Approved",
  completed: "Completed",
  in_progress: "In Progress",
  not_started: "Not Started",
  expired: "Expired",
  rejected: "Rejected",
};

const getStatusBadgeClass = (status = "") => {
  const normalized = status.toLowerCase();
  switch (normalized) {
    case "completed":
    case "approved":
      return "badge bg-success-subtle text-success";
    case "pending_review":
      return "badge bg-warning-subtle text-warning";
    case "rejected":
    case "expired":
      return "badge bg-danger-subtle text-danger";
    case "in_progress":
      return "badge bg-info-subtle text-info";
    default:
      return "badge bg-light text-dark";
  }
};

const getCourseLabel = (course, cert) => {
  if (course) {
    return course.title || course.name || course.courseTitle || course.displayName || cert.courseKey || "Untitled Course";
  }
  return cert.courseTitle || cert.courseKey || "Untitled Course";
  return cert.courseTitle || cert.courseKey || "Untitled Course";
};

// Check if date is overdue
function isOverdue(dateString, isCompleted = false) {
  return CertificationServices.isOverdue(dateString, isCompleted);
}

function isExpired(dateString) {
  return CertificationServices.isExpired(dateString);
}

const SchoolAdminTeacherCertificationsDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasPreselectedRef = useRef(false);
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [customTrainingCourses, setCustomTrainingCourses] = useState([]);
  const [hasNewCourses, setHasNewCourses] = useState(false);
  const [selectedTeachers, setSelectedTeachers] = useState(new Set());
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddCourseForm, setShowAddCourseForm] = useState(false);
  const [showDeleteCourseForm, setShowDeleteCourseForm] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [newCourseData, setNewCourseData] = useState({
    title: "",
    description: "",
    category: "Custom Courses",
    defaultHours: "",
    isRequired: false,
  });
  const [assignFormData, setAssignFormData] = useState({
    courseKey: "",
    dueByDate: "",
    renewByDate: "",
    hours: "",
    notes: "",
    status: "not_started",
  });
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  // Which export is running, so only that button shows a spinner.
  const [exporting, setExporting] = useState(null);

  // Sort courses alphabetically for flat display
  const sortedCourses = useMemo(() => {
    return [...courses].sort((a, b) => {
      const nameA = a.title || a.name || a.courseTitle || a.displayName || "";
      const nameB = b.title || b.name || b.courseTitle || b.displayName || "";
      return nameA.localeCompare(nameB);
    });
  }, [courses]);

  // Load teachers and courses
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load teachers under this school
      const schoolCertResponse = await CertificationServices.getSchoolCertifications();
      console.log("School cert response:", schoolCertResponse);

      // Handle new response structure
      const teacherRecords = extractTeacherRecords(schoolCertResponse.teachers || schoolCertResponse);
      console.log("Extracted teacher records:", teacherRecords);
      setTeachers(teacherRecords);

      // Set custom training courses state
      const customCoursesFromBackend = schoolCertResponse.customTrainingCourses || [];
      if (customCoursesFromBackend.length > 0) {
        setCustomTrainingCourses(customCoursesFromBackend);
        setHasNewCourses(schoolCertResponse.hasNewCourses || false);
      }

      // Load available courses
      const coursesResponse = await CertificationServices.getCourses();
      console.log("Courses response:", coursesResponse);
      let coursesList = Array.isArray(coursesResponse?.courses)
        ? coursesResponse.courses
        : Array.isArray(coursesResponse)
          ? coursesResponse
          : [];
      console.log("Courses list:", coursesList);

      // Debug: Check which courses are missing keys
      const coursesWithoutKeys = coursesList.filter(c => !c.key && !c.courseKey);
      if (coursesWithoutKeys.length > 0) {
        console.warn(`⚠️ Found ${coursesWithoutKeys.length} courses without key fields:`, coursesWithoutKeys.map(c => ({ _id: c._id, title: c.title })));
      }

      // Check for Angelwatch Core courses
      const angelwatchCoreGroup = GROUP_DEFINITIONS.find(g => g.name === "Angelwatch Core");
      const angelwatchCoreKeys = angelwatchCoreGroup ? new Set(angelwatchCoreGroup.keys) : new Set();

      // Mark Angelwatch Core courses as required by default if not already set in DB
      // Other validation logic...

      // Merge custom training courses into coursesList (use data from response, not state)
      console.log("Custom training courses from backend:", customCoursesFromBackend);
      if (customCoursesFromBackend.length > 0) {
        const customCoursesFormatted = customCoursesFromBackend.map(course => ({
          key: `custom-training-${course.id}`,
          courseKey: `custom-training-${course.id}`,
          title: course.title,
          name: course.title,
          description: course.description,
          defaultHours: course.hours,
          isRequired: course.isRequired,
          source: 'angelwatch',
          isNew: course.isNew,
          category: 'Angelwatch Training'
        }));
        console.log("Formatted custom training courses:", customCoursesFormatted);
        coursesList = [...coursesList, ...customCoursesFormatted];
        console.log("Final courses list with custom training:", coursesList);
      }

      setCourses(coursesList);
    } catch (error) {
      console.error("Failed to load data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to load teacher data: ${error.message || "Please try again."}`,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Pre-select teacher from query parameter
  useEffect(() => {
    const teacherIdFromUrl = searchParams.get("teacherId");
    if (teacherIdFromUrl && teachers.length > 0 && !hasPreselectedRef.current) {
      // Convert both to strings for comparison (teacherId might be ObjectId or string)
      const teacherIdStr = String(teacherIdFromUrl);
      // Check if the teacher exists in the list
      const teacherExists = teachers.some((t) => String(t.teacherId) === teacherIdStr);
      if (teacherExists) {
        console.log("Pre-selecting teacher from URL:", teacherIdStr);
        setSelectedTeachers(new Set([teacherIdStr]));
        hasPreselectedRef.current = true;
        // Remove the query parameter from URL after selecting
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("teacherId");
        setSearchParams(newSearchParams, { replace: true });
      } else {
        console.log("Teacher not found in list. URL teacherId:", teacherIdStr, "Available teachers:", teachers.map(t => String(t.teacherId)));
      }
    }
  }, [teachers, searchParams, setSearchParams]);

  const extractTeacherRecords = useCallback((payload) => {
    console.log("Extracting teacher records from payload:", payload);
    const list = (() => {
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.certifications)) return payload.certifications;
      if (Array.isArray(payload?.teachers)) return payload.teachers;
      if (Array.isArray(payload?.records)) return payload.records;
      if (Array.isArray(payload?.data)) return payload.data;
      return [];
    })();

    console.log("Extracted list:", list);

    const records = list
      .map((entry) => {
        const teacher =
          entry.teacher || entry.teacherInfo || entry.user || entry.profile || {};
        // Backend returns certifications in "records" field, not "certifications"
        // For School Admin, certifications are in "certifications" field
        const certifications = Array.isArray(entry.records)
          ? entry.records
          : Array.isArray(entry.certifications)
            ? entry.certifications
            : [];
        const teacherId =
          teacher.id ||
          teacher._id ||
          entry.teacherId ||
          entry.userId ||
          entry.id ||
          "";
        if (!teacherId) {
          console.warn("No teacherId found for entry:", entry);
          return null;
        }

        // Include all entries (both User teachers and School Admin)
        // School Admin has School ID, regular teachers have User ID
        // Backend handles both types correctly

        return {
          teacherId: String(teacherId), // Ensure consistent string format
          teacherName:
            teacher.name ||
            teacher.fullName ||
            `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() ||
            entry.teacherName ||
            "Unknown Teacher",
          teacherEmail: teacher.email || entry.teacherEmail || "",
          dateJoined: teacher.createdAt || teacher.dateJoined || entry.createdAt || entry.dateJoined || new Date().toISOString(),
          certifications,
        };
      })
      .filter(Boolean);

    console.log("Final teacher records:", records);
    return records;
  }, []);

  // Filter teachers based on search
  // Filter teachers based on search and selection
  const filteredTeachers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let result = teachers;

    // 1. Filter by Selection (if any are selected)
    if (selectedTeachers.size > 0) {
      result = result.filter((teacher) => selectedTeachers.has(String(teacher.teacherId)));
    }

    // 2. Filter by Search Term
    if (term) {
      result = result.filter(
        (teacher) =>
          teacher.teacherName.toLowerCase().includes(term) ||
          teacher.teacherEmail.toLowerCase().includes(term)
      );
    }

    return result;
  }, [teachers, searchTerm, selectedTeachers]);

  // Calculate global statistics across filtered teachers
  const globalStats = useMemo(() => {
    const stats = {
      totalAssigned: 0,
      completed: 0,
      inProgress: 0,
      overdue: 0,
      expired: 0
    };

    filteredTeachers.forEach(teacher => {
      const certs = teacher.certifications || [];
      stats.totalAssigned += certs.length;

      certs.forEach(cert => {
        const status = (cert.status || "").toLowerCase();
        const isCompleted = ["completed", "approved"].includes(status);

        if (isCompleted) {
          stats.completed += 1;
        } else if (status === "in_progress") {
          stats.inProgress += 1;
        }

        // Check overdue (not completed and past due date)
        if (isOverdue(cert.dueByDate, isCompleted)) {
          stats.overdue += 1;
        }

        // Check expired (past renew date)
        if (isExpired(cert.renewByDate)) {
          stats.expired += 1;
        }
      });
    });

    return stats;
  }, [filteredTeachers]);

  // Teacher options for multi-select dropdown
  const teacherOptions = useMemo(() => {
    return teachers.map((teacher) => ({
      value: String(teacher.teacherId), // Ensure value is always a string for consistent comparison
      label: `${teacher.teacherName}${teacher.teacherEmail ? ` (${teacher.teacherEmail})` : ""}`,
    }));
  }, [teachers]);

  // Selected teachers for dropdown (array format)
  const selectedTeacherOptions = useMemo(() => {
    return teacherOptions.filter((option) => selectedTeachers.has(String(option.value)));
  }, [teacherOptions, selectedTeachers]);

  // Handle teacher selection from dropdown
  const handleTeacherSelectChange = (selectedOptions) => {
    const newSelected = new Set(selectedOptions.map((opt) => String(opt.value)));
    setSelectedTeachers(newSelected);
  };

  // Handle select all teachers
  const handleSelectAllTeachers = () => {
    if (selectedTeachers.size === teachers.length) {
      setSelectedTeachers(new Set());
    } else {
      setSelectedTeachers(new Set(teachers.map((t) => String(t.teacherId))));
    }
  };

  // Handle course change
  const handleCourseChange = (event) => {
    setAssignFormData({
      ...assignFormData,
      courseKey: event.target.value,
    });
  };

  // Calculate default dates for Angelwatch Core courses
  // Assign certification to selected teachers
  const handleAssignCertification = async () => {
    if (!assignFormData.courseKey) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please select a course.",
      });
      return;
    }

    if (selectedTeachers.size === 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please select at least one teacher.",
      });
      return;
    }

    try {
      setIsLoading(true);
      const promises = Array.from(selectedTeachers).map((teacherId) => {
        const payload = {
          courseKey: assignFormData.courseKey,
          status: assignFormData.status || "not_started",
        };

        // Blank means blank. These fields used to fall back to
        // calculateDefaultDates() when left empty, so clearing a due date was the
        // exact action that re-populated it from a hardcoded 2026 calendar - the
        // admin could never remove a deadline they had not set.
        if (assignFormData.dueByDate) {
          payload.dueByDate = assignFormData.dueByDate;
        } else {
          payload.dueByDate = null;
        }

        if (assignFormData.renewByDate) {
          payload.renewByDate = assignFormData.renewByDate;
        } else {
          payload.renewByDate = null;
        }

        // Don't set dateCompleted - that's for when they actually complete it
        // payload.dateCompleted is intentionally not set

        if (assignFormData.hours) {
          payload.hours = parseFloat(assignFormData.hours);
        }
        if (assignFormData.notes) {
          payload.notes = assignFormData.notes;
        }

        return CertificationServices.assignCertificationToTeacher(teacherId, payload);
      });

      await Promise.all(promises);

      Swal.fire({
        icon: "success",
        title: "Success",
        text: `Certification assigned to ${selectedTeachers.size} teacher(s).`,
      });

      // Reset form and selection
      setSelectedTeachers(new Set());
      setAssignFormData({
        courseKey: "",
        dueByDate: "",
        renewByDate: "",
        hours: "",
        notes: "",
        status: "not_started",
      });
      await loadData();
    } catch (error) {
      console.error("Error assigning certifications:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.error || "Failed to assign certifications. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get certification summary for a teacher
  const getCertificationSummary = (certifications) => {
    return certifications.reduce(
      (summary, cert) => {
        const status = (cert.status || "").toLowerCase();
        if (["completed", "approved"].includes(status)) {
          summary.completed += 1;
        } else if (status === "pending_review") {
          summary.pendingReview += 1;
        } else if (status === "in_progress") {
          summary.inProgress += 1;
        } else if (status === "expired") {
          summary.expired += 1;
        }
        return summary;
      },
      {
        total: certifications.length,
        completed: 0,
        pendingReview: 0,
        inProgress: 0,
        expired: 0,
      }
    );
  };

  // Group certifications by displayGroup (like CertificationTracker)
  const groupCertificationsByGroup = (certifications) => {
    const grouped = {};
    certifications.forEach((cert) => {
      const groupName = cert.displayGroup || "Other Certifications";
      if (!grouped[groupName]) {
        grouped[groupName] = {
          name: groupName,
          priority: cert.displayPriority || 999,
          certifications: [],
        };
      }
      grouped[groupName].certifications.push(cert);
    });

    // Sort certifications within each group by displayOrder
    Object.keys(grouped).forEach((groupName) => {
      grouped[groupName].certifications.sort((a, b) => {
        const orderA = a.displayOrder || 0;
        const orderB = b.displayOrder || 0;
        return orderA - orderB;
      });
    });

    return grouped;
  };

  // Get sorted group names
  const getSortedGroupNames = (groupedCertifications) => {
    const groupNames = Object.keys(groupedCertifications);
    return groupNames.sort((a, b) => {
      const priorityA = groupedCertifications[a].priority || 999;
      const priorityB = groupedCertifications[b].priority || 999;
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return a.localeCompare(b);
    });
  };

  // Handle certificate download
  const handleDownloadCertificate = async (cert, teacherId, fallbackCourseName) => {
    if (!cert?.certificateUrl) {
      Swal.fire(
        "No certificate",
        "There is no certificate file saved for this course yet.",
        "info"
      );
      return;
    }

    const identifier = cert._id || cert.id || `${cert.courseKey || "record"}-certificate`;
    setDownloadingIds((prev) => new Set(prev).add(identifier));
    try {
      const safeCourseLabel = (fallbackCourseName || "Certificate").replace(
        /[\\/:*?"<>|]+/g,
        "_"
      );
      const { blob, filename, contentType } =
        await CertificationServices.downloadCertificate({
          certId: cert._id || cert.id,
          teacherId: teacherId,
          fallbackName: `${safeCourseLabel}.pdf`,
        });
      const blobUrl = window.URL.createObjectURL(
        new Blob([blob], { type: contentType })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || `${safeCourseLabel}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download certificate", error);
      Swal.fire(
        "Download failed",
        "We couldn't download the certificate. Please try again.",
        "error"
      );
    } finally {
      setDownloadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(identifier);
        return newSet;
      });
    }
  };

  /**
   * Download a roster report or a batch of printable certificates.
   *
   * Scope follows what is already on screen: the staff picker above doubles as
   * this table's filter, so exporting "the selected teachers" and "the rows I
   * am looking at" are the same thing. An empty selection means everyone,
   * which is what the unfiltered table shows.
   *
   * `onlyTeacherId` overrides all of that for the per-teacher Print button, so
   * printing one person's certificates does not require first selecting them
   * in a picker that lives in a different card.
   *
   * @param {"csv"|"pdf"|"certificates"} kind
   * @param {string} [onlyTeacherId]
   */
  const handleExport = async (kind, onlyTeacherId) => {
    const teacherIds = onlyTeacherId
      ? [onlyTeacherId]
      : Array.from(selectedTeachers);
    // Key the spinner by teacher too, so one person's Print button does not
    // put every other row into a loading state.
    setExporting(onlyTeacherId ? `${kind}:${onlyTeacherId}` : kind);
    try {
      const { blob, filename } =
        kind === "certificates"
          ? await CertificationServices.exportSchoolCertificates(teacherIds)
          : await CertificationServices.exportSchoolReport(kind, teacherIds);
      saveBlob(blob, filename);
    } catch (error) {
      console.error(`Failed to export ${kind}`, error);
      // The server explains the one case worth explaining - nobody in the
      // selection has finished a course, so there is nothing to certify. Show
      // that rather than a generic failure, or the administrator retries.
      const serverMessage = await readBlobError(error);
      Swal.fire(
        serverMessage ? "Nothing to print" : "Export failed",
        serverMessage ||
          "We couldn't build that file. Please try again, or select fewer staff.",
        serverMessage ? "info" : "error"
      );
    } finally {
      setExporting(null);
    }
  };

  // Handle adding custom course
  const handleAddCustomCourse = async () => {
    if (!newCourseData.title.trim()) {
      alert("Please enter a course title.");
      return;
    }

    setIsLoading(true);
    try {
      // Generate a simplified course key
      const sanitizedTitle = newCourseData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      const courseKey = "custom-" + (sanitizedTitle || "unnamed") + "-" + Date.now();

      const payload = {
        key: courseKey,
        title: newCourseData.title.trim(),
        description: newCourseData.description.trim() || "",
        category: "Custom Courses",
        defaultHours: newCourseData.defaultHours ? parseFloat(newCourseData.defaultHours) : undefined,
        isRequired: false, // User requested this be assignment-only, not global required
        source: "custom",
      };

      // Call API to create course
      await CertificationServices.createCustomCourse(payload);

      let assignedCount = 0;

      // Logic: If there are selected teachers, automatically assign the course to them
      if (selectedTeachers.size > 0) {
        console.log(`Auto-assigning new course ${courseKey} to ${selectedTeachers.size} selected teachers...`);
        try {
          const assignmentPromises = Array.from(selectedTeachers).map(teacherId => {
            return CertificationServices.assignCertificationToTeacher(teacherId, {
              courseKey: courseKey,
              dueByDate: null,
              renewByDate: null,
              notes: "Auto-assigned upon creation",
              hours: payload.defaultHours
            });
          });

          await Promise.all(assignmentPromises);
          assignedCount = selectedTeachers.size;
        } catch (assignError) {
          console.error("Error auto-assigning course:", assignError);
          Swal.fire({
            icon: "warning",
            title: "Course Created, But Assignment Failed",
            text: "The custom course was created, but we couldn't auto-assign it to the selected teachers.",
          });
          return;
        }
      }

      Swal.fire({
        icon: "success",
        title: "Success",
        text: assignedCount > 0
          ? `Custom course created and assigned to ${assignedCount} selected teacher(s)!`
          : "Custom course created successfully!",
      });

      // Reset form and close inline form
      setNewCourseData({
        title: "",
        description: "",
        category: "Custom Courses",
        defaultHours: "",
        isRequired: false,
      });
      setShowAddCourseForm(false);

      // Reload courses
      await loadData();
    } catch (error) {
      console.error("Error creating custom course:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.error || "Failed to create custom course. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCustomCourse = async () => {
    if (!courseToDelete) return;

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You are about to delete this custom course. This will also remove the course from ALL assigned teachers who haven't completed it yet. This action cannot be undone!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        await CertificationServices.deleteCustomCourse(courseToDelete.value);

        await Swal.fire(
          'Deleted!',
          'The custom course has been deleted.',
          'success'
        );

        // Reset and refresh
        setCourseToDelete(null);
        setShowDeleteCourseForm(false);
        const coursesData = await CertificationServices.getCourses();
        if (coursesData.data && coursesData.data.courses) {
          setCourses(coursesData.data.courses);
        }
        await loadData();
      } catch (error) {
        console.error("Error deleting custom course:", error);
        Swal.fire(
          'Error!',
          error.response?.data?.error || 'Failed to delete custom course.',
          'error'
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle deleting a single assignment
  const handleDeleteAssignment = async (certId, courseTitle, teacherName) => {
    const result = await Swal.fire({
      title: 'Remove Assignment?',
      text: `Are you sure you want to remove "${courseTitle}" from ${teacherName}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove it!'
    });

    if (result.isConfirmed) {
      try {
        await CertificationServices.deleteAssignment(certId);

        await Swal.fire(
          'Removed!',
          'The assignment has been removed.',
          'success'
        );

        // Refresh data
        await loadData();
      } catch (error) {
        console.error("Error removing assignment:", error);
        Swal.fire(
          'Error!',
          error.response?.data?.error || 'Failed to remove assignment.',
          'error'
        );
      }
    }
  };

  // Handle bulk course assignment
  const handleBulkAssign = async () => {
    if (selectedTeachers.size === 0) {
      Swal.fire("Selection Missing", "Please select at least one teacher.", "warning");
      return;
    }
    if (selectedCourses.length === 0) {
      Swal.fire("Selection Missing", "Please select at least one course.", "warning");
      return;
    }

    const confirm = await Swal.fire({
      title: `Assign ${selectedCourses.length} Courses?`,
      text: `Assigning to ${selectedTeachers.size} selected teachers.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Assign",
      cancelButtonText: "Cancel"
    });

    if (!confirm.isConfirmed) return;

    setIsLoading(true);
    try {
      const promises = [];
      selectedCourses.forEach(courseOpt => {
        const courseKey = courseOpt.value;
        // Find full course object to get default hours
        const fullCourse = courses.find(c => (c.key || c.courseKey) === courseKey);
        // The course's own default, used only when the admin typed nothing.
        const rawHours = assignFormData.hours !== "" && assignFormData.hours != null
          ? assignFormData.hours
          : (fullCourse?.defaultHours ?? fullCourse?.hours);
        const n = Number(rawHours);
        const parsedHours = rawHours == null || rawHours === "" || !Number.isFinite(n)
          ? null
          : n;

        selectedTeachers.forEach(teacherId => {
          promises.push(CertificationServices.assignCertificationToTeacher(teacherId, {
            courseKey,
            // null, not "". Blank means blank here exactly as it does in the
            // single-assign form above; an empty string is not a date and is
            // not a number, and sending one for hours was what made every
            // re-assignment fail with "Failed to assign courses".
            dueByDate: assignFormData.dueByDate || null,
            renewByDate: assignFormData.renewByDate || null,
            notes: assignFormData.notes || "Bulk assignment via dashboard",
            hours: parsedHours
          }));
        });
      });

      await Promise.all(promises);

      Swal.fire({
        icon: "success",
        title: "Assignments Complete",
        text: `Successfully assigned ${selectedCourses.length} courses to ${selectedTeachers.size} teachers.`,
        timer: 2000
      });

      setSelectedCourses([]);
      await loadData(); // Reload to show newly assigned courses
    } catch (error) {
      console.error("Bulk assign error", error);
      // The server says what it refused and why. Swallowing it behind a fixed
      // string is why a type error on `hours` read to an admin as the whole
      // feature being broken, with nothing to act on.
      Swal.fire(
        "Error",
        error?.response?.data?.error || "Failed to assign courses.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };





  // Handle editing assignment dates
  const handleEditAssignment = async (cert, teacherId, teacherName) => {
    const defaultDue = cert.dueByDate ? cert.dueByDate.split('T')[0] : '';
    const defaultRenew = cert.renewByDate ? cert.renewByDate.split('T')[0] : '';
    const defaultNotes = cert.notes || '';

    const { value: formValues } = await Swal.fire({
      title: 'Update Assignment',
      html: `
        <div class="mb-3 text-start">
          <label class="form-label">Due By Date</label>
          <input id="swal-due-date" class="form-control" type="date" value="${defaultDue}">
        </div>
        <div class="mb-3 text-start">
          <label class="form-label">Renew By Date</label>
          <input id="swal-renew-date" class="form-control" type="date" value="${defaultRenew}">
        </div>
        <div class="mb-3 text-start">
          <label class="form-label">Notes</label>
          <textarea id="swal-notes" class="form-control" rows="2">${defaultNotes}</textarea>
        </div>
        <div class="text-muted small text-start">
           Note: Dates will be updated. Clearing a date without setting a new one may default to the existing date.
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Update',
      preConfirm: () => {
        return {
          dueByDate: document.getElementById('swal-due-date').value,
          renewByDate: document.getElementById('swal-renew-date').value,
          notes: document.getElementById('swal-notes').value
        }
      }
    });

    if (formValues) {
      try {
        setIsLoading(true);
        await CertificationServices.assignCertificationToTeacher(teacherId, {
          courseKey: cert.courseKey,
          dueByDate: formValues.dueByDate,
          renewByDate: formValues.renewByDate,
          notes: formValues.notes
          // hours is preserved by backend logic if omitted
        });

        await Swal.fire({
          icon: 'success',
          title: 'Updated',
          text: 'Certification updated successfully.',
          timer: 1500
        });

        await loadData();
      } catch (error) {
        console.error("Error updating certification:", error);
        Swal.fire({
          icon: 'error',
          title: 'Update Failed',
          text: error.response?.data?.error || "Failed to update.",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const userobj = JSON.parse(localStorage.getItem("user") || "{}");

  return (
    <DashboardLayout
      role={1}
      userType={userobj?.type}
      activeMenu="Teacher Certifications"
      layoutType="topnav"
    >
      <div className="card">
        <div className="card-body">
          {/* New Courses Notification Banner */}
          {hasNewCourses && (
            <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
              <i className="bx bx-bell fs-4 me-3"></i>
              <div className="flex-grow-1">
                <strong>New training courses are now available!</strong>
              </div>
            </div>
          )}

          <div className="d-flex flex-column flex-lg-row gap-3 justify-content-between align-items-lg-center mb-4">
            <div>
              <h4 className="mb-1">Teacher Certifications Dashboard</h4>
              <p className="text-muted mb-0">
                Manage certifications for all teachers under your school. Select teachers and assign required training or custom certificates.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <input
                type="search"
                className="form-control"
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ minWidth: "200px" }}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={loadData}
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : "Refresh"}
              </button>
            </div>
          </div>

          {/* Global Stats Summary */}
          <div className="row g-3 mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card h-100 border-0 shadow-sm bg-success-subtle">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className="avatar-sm rounded-circle bg-success text-white d-flex align-items-center justify-content-center me-3">
                      <i className="bx bx-check-double fs-4"></i>
                    </div>
                    <h6 className="card-title mb-0 text-success">Completed</h6>
                  </div>
                  <h3 className="mb-0">{globalStats.completed}</h3>
                  <small className="text-muted">Total Finished</small>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card h-100 border-0 shadow-sm bg-info-subtle">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className="avatar-sm rounded-circle bg-info text-white d-flex align-items-center justify-content-center me-3">
                      <i className="bx bx-loader fs-4"></i>
                    </div>
                    <h6 className="card-title mb-0 text-info">In Progress</h6>
                  </div>
                  <h3 className="mb-0">{globalStats.inProgress}</h3>
                  <small className="text-muted">Currently Active</small>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card h-100 border-0 shadow-sm bg-danger-subtle">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className="avatar-sm rounded-circle bg-danger text-white d-flex align-items-center justify-content-center me-3">
                      <i className="bx bx-error-circle fs-4"></i>
                    </div>
                    <h6 className="card-title mb-0 text-danger">Overdue</h6>
                  </div>
                  <h3 className="mb-0">{globalStats.overdue}</h3>
                  <small className="text-muted">Missed Due Date</small>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card h-100 border-0 shadow-sm bg-warning-subtle">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className="avatar-sm rounded-circle bg-warning text-white d-flex align-items-center justify-content-center me-3">
                      <i className="bx bx-time fs-4"></i>
                    </div>
                    <h6 className="card-title mb-0 text-warning">Expired / Renew</h6>
                  </div>
                  <h3 className="mb-0">{globalStats.expired}</h3>
                  <small className="text-muted">Needs Renewal</small>
                </div>
              </div>
            </div>
          </div>

          {/* Print & Export
              Answers the request that staff should not each have to sign in on
              the administrator's computer to get their certificate. The
              certificates PDF prints in black and white by design - most
              centres have no colour printer. */}
          <div className="card mb-4">
            <div className="card-header d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Print &amp; Export</h5>
              <small className="text-muted">
                {selectedTeachers.size > 0
                  ? `${selectedTeachers.size} staff selected`
                  : `All ${teachers.length} staff`}
              </small>
            </div>
            <div className="card-body">
              <p className="text-muted mb-3">
                Download records for{" "}
                {selectedTeachers.size > 0
                  ? "the staff selected below"
                  : "everyone at your centre"}
                . Select staff in <strong>Assign Certification</strong> to narrow
                any of these.
              </p>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleExport("certificates")}
                  disabled={exporting !== null || teachers.length === 0}
                  title="One certificate per page, for completed courses only. Prints in black and white."
                >
                  <i className="bx bx-printer me-1"></i>
                  {exporting === "certificates"
                    ? "Preparing certificates..."
                    : "Print Certificates (PDF)"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => handleExport("csv")}
                  disabled={exporting !== null || teachers.length === 0}
                  title="Spreadsheet of every staff member and course, including unfinished ones."
                >
                  <i className="bx bx-spreadsheet me-1"></i>
                  {exporting === "csv" ? "Preparing..." : "Training Report (CSV)"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => handleExport("pdf")}
                  disabled={exporting !== null || teachers.length === 0}
                  title="Printable table of who has completed what."
                >
                  <i className="bx bx-file me-1"></i>
                  {exporting === "pdf" ? "Preparing..." : "Training Report (PDF)"}
                </button>
              </div>
              <small className="text-muted d-block mt-3">
                <i className="bx bx-info-circle me-1"></i>
                A certificate is only issued for a course a staff member has
                completed. The training report lists everyone, including courses
                still outstanding.
              </small>
            </div>
          </div>

          {/* Assignment Form */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Assign Certification</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-12">
                  <label className="form-label">
                    Select Teachers <span className="text-danger">*</span>
                  </label>
                  <div className="d-flex gap-2 mb-2">
                    <Select
                      isMulti
                      options={teacherOptions}
                      value={selectedTeacherOptions}
                      onChange={handleTeacherSelectChange}
                      placeholder="Select one or more teachers..."
                      className="flex-grow-1"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "38px",
                        }),
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleSelectAllTeachers}
                      disabled={teachers.length === 0}
                    >
                      {selectedTeachers.size === teachers.length && teachers.length > 0
                        ? "Deselect All"
                        : "Select All"}
                    </button>
                  </div>
                  {selectedTeachers.size > 0 && (
                    <small className="text-muted">
                      <i className="bx bx-user-check me-1"></i>
                      {selectedTeachers.size} teacher(s) selected
                    </small>
                  )}
                </div>
                <div className="col-md-12">
                  <div
                    role="button"
                    tabIndex={0}
                    className={`btn ${showAddCourseForm ? 'btn-outline-danger' : 'btn-outline-success'}`}
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowAddCourseForm(!showAddCourseForm);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowAddCourseForm(!showAddCourseForm);
                      }
                    }}
                  >
                    <i className={`bx ${showAddCourseForm ? 'bx-minus' : 'bx-plus'} me - 1`}></i>
                    {showAddCourseForm ? "Cancel Adding Course" : "Add New Custom Course"}
                  </div>

                  <div
                    role="button"
                    tabIndex={0}
                    className={`btn ms - 2 ${showDeleteCourseForm ? 'btn-outline-secondary' : 'btn-outline-danger'}`}
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowDeleteCourseForm(!showDeleteCourseForm);
                      setShowAddCourseForm(false); // Close add form if open
                    }}
                  >
                    <i className={`bx ${showDeleteCourseForm ? 'bx-x' : 'bx-trash'} me - 1`}></i>
                    {showDeleteCourseForm ? "Cancel Delete" : "Delete Custom Course"}
                  </div>

                  {/* Inline Delete Custom Course Form */}
                  {showDeleteCourseForm && (
                    <div className="card mt-3 border-danger mb-3">
                      <div className="card-header bg-transparent border-danger text-danger">
                        <i className="bx bx-trash me-1"></i> Delete Custom Course
                      </div>
                      <div className="card-body">
                        <div className="row g-3 align-items-end">
                          <div className="col-md-9">
                            <label className="form-label">Select Course to Delete</label>
                            <Select
                              options={courses
                                .filter(c => c.source === 'custom' && c.category === 'Custom Courses')
                                .map(c => ({
                                  value: c.key,
                                  label: c.title
                                }))}
                              value={courseToDelete}
                              onChange={setCourseToDelete}
                              placeholder="Select a custom course to delete..."
                              isClearable
                            />
                            {courses.filter(c => c.source === 'custom').length === 0 && (
                              <div className="form-text text-muted mt-1">No custom courses found.</div>
                            )}
                          </div>
                          <div className="col-md-3">
                            <button
                              type="button"
                              className="btn btn-danger w-100"
                              onClick={handleDeleteCustomCourse}
                              disabled={!courseToDelete || isLoading}
                            >
                              <i className="bx bx-trash me-1"></i> Delete Permanently
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Inline Add Custom Course Form */}
                  {showAddCourseForm && (
                    <div className="card mt-3 border-success mb-3">
                      <div className="card-header bg-transparent border-success text-success">
                        <i className="bx bx-plus-circle me-1"></i> Add New Custom Course
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-12">
                            {selectedTeachers.size > 0 && (
                              <div className="alert alert-info py-2 mb-3">
                                <i className="bx bx-info-circle me-1"></i>
                                <strong>Auto-Assign:</strong> This course will be immediately assigned to the <strong>{selectedTeachers.size}</strong> teachers selected above.
                              </div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Course Title <span className="text-danger">*</span></label>
                            <input type="text" className="form-control" value={newCourseData.title} onChange={(e) => setNewCourseData({ ...newCourseData, title: e.target.value })} placeholder="Enter course title" required />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Default Hours</label>
                            <input type="number" min="0" step="0.1" className="form-control" value={newCourseData.defaultHours} onChange={(e) => setNewCourseData({ ...newCourseData, defaultHours: e.target.value })} placeholder="e.g. 1.5" />
                          </div>
                          <div className="col-md-12">
                            <label className="form-label">Description</label>
                            <textarea className="form-control" rows="2" value={newCourseData.description} onChange={(e) => setNewCourseData({ ...newCourseData, description: e.target.value })} placeholder="Brief description..." />
                          </div>
                        </div>
                        <div className="col-md-12 text-end">
                          <button type="button" className="btn btn-secondary me-2" onClick={() => setShowAddCourseForm(false)}>Cancel</button>
                          <button type="button" className="btn btn-primary" onClick={handleAddCustomCourse} disabled={isLoading || !newCourseData.title.trim()}>Create & Assign</button>
                        </div>
                      </div>
                    </div>

                  )}
                </div>
              </div>
              <div className="col-md-12">
                <label className="form-label">
                  Select Courses to Assign <span className="text-danger">*</span>
                </label>
                <Select
                  isMulti
                  options={sortedCourses.map(c => {
                    const courseLabel = c.title || c.name || c.courseTitle || c.displayName || (c.key || c.courseKey);
                    const label = c.isNew ? `${courseLabel} ⭐ NEW` : courseLabel;
                    return {
                      value: c.key || c.courseKey,
                      label: label
                    };
                  })}
                  value={selectedCourses}
                  onChange={setSelectedCourses}
                  placeholder="Select courses..."
                />
              </div>
              <div className="col-md-12">
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  onClick={handleBulkAssign}
                  disabled={selectedTeachers.size === 0 || selectedCourses.length === 0 || isLoading}
                >
                  {isLoading ? "Assigning..." : `Assign ${selectedCourses.length} Courses to ${selectedTeachers.size} Teachers`}
                </button>
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Due by date
                  <small className="text-muted ms-2">(Optional - leave blank for no deadline)</small>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={assignFormData.dueByDate}
                  onChange={(e) =>
                    setAssignFormData({
                      ...assignFormData,
                      dueByDate: e.target.value,
                    })
                  }
                />
                <small className="form-text text-muted">
                  Leave blank for automatic: Core courses staggered 30 days apart, others 30 days from assignment
                </small>
              </div>
              <div className="col-md-6">
                <label className="form-label">
                  Renew by date
                  <small className="text-muted ms-2">(Optional)</small>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={assignFormData.renewByDate}
                  onChange={(e) =>
                    setAssignFormData({
                      ...assignFormData,
                      renewByDate: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Hours spent</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  className="form-control"
                  value={assignFormData.hours}
                  onChange={(e) =>
                    setAssignFormData({
                      ...assignFormData,
                      hours: e.target.value,
                    })
                  }
                  placeholder="e.g. 1.5"
                />
              </div>
              <div className="col-12">
                <label className="form-label">
                  Notes for reviewers
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={assignFormData.notes}
                  onChange={(e) =>
                    setAssignFormData({
                      ...assignFormData,
                      notes: e.target.value,
                    })
                  }
                  placeholder="Add context for school administrators (optional)"
                />
              </div>

            </div>
          </div>
        </div>

        {/* Debug info - remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div className="alert alert-info mb-3">
            <small>
              Debug: isLoading={isLoading ? 'true' : 'false'},
              teachers.length={teachers.length},
              courses.length={courses.length}
            </small>
          </div>
        )}

        {/* Teachers with certification tables */}
        {isLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading teachers...</p>
          </div>
        ) : filteredTeachers.length === 0 ? (
          <div className="text-center text-muted py-4">
            <p>No teachers found.</p>
            {teachers.length === 0 && (
              <p className="small">No teachers are registered under your school yet.</p>
            )}
            {teachers.length > 0 && filteredTeachers.length === 0 && (
              <p className="small">No teachers match your search criteria.</p>
            )}
          </div>
        ) : (
          <div className="row g-4">
            {console.log("Rendering teachers:", filteredTeachers)}
            {filteredTeachers.map((teacher) => {
              console.log("Rendering teacher:", teacher.teacherName, teacher.teacherId);
              try {
                const summary = getCertificationSummary(teacher.certifications || []);
                const groupedCerts = groupCertificationsByGroup(teacher.certifications || []);
                const sortedGroupNames = getSortedGroupNames(groupedCerts);

                return (
                  <div key={teacher.teacherId} className="col-12">
                    <div className="card">
                      <div className="card-header">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div>
                            <h5 className="mb-0 text-primary fw-bold">{teacher.teacherName}</h5>
                            {teacher.teacherEmail && (
                              <small className="text-muted">
                                <a
                                  href={`mailto: ${teacher.teacherEmail}`}
                                  className="text-decoration-none text-secondary"
                                >
                                  {teacher.teacherEmail}
                                </a>
                              </small>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-3">
                            {/* Print this one person's certificates. The card
                                export can do it via the staff picker, but that
                                picker lives in another card - and "print the
                                certificate for this teacher" is the thing
                                administrators actually came here to do.
                                Disabled with nothing completed, so the button
                                never leads to an empty-handed 404. */}
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleExport("certificates", teacher.teacherId)}
                              disabled={
                                exporting !== null || summary.completed === 0
                              }
                              title={
                                summary.completed === 0
                                  ? "No completed courses to print yet"
                                  : `Print ${summary.completed} certificate(s) for ${teacher.teacherName}`
                              }
                            >
                              <i className="bx bx-printer me-1"></i>
                              {exporting === `certificates:${teacher.teacherId}`
                                ? "Preparing..."
                                : "Print Certificates"}
                            </button>
                            <div className="text-end">
                              <h4 className="mb-0 fw-bold text-success">
                                {summary.total > 0 ? Math.round((summary.completed / summary.total) * 100) : 0}%
                              </h4>
                              <small className="text-muted">Completion</small>
                            </div>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="progress mb-3" style={{ height: "10px" }}>
                          <div
                            className="progress-bar bg-success"
                            role="progressbar"
                            style={{ width: `${summary.total > 0 ? (summary.completed / summary.total) * 100 : 0}%` }}
                            aria-valuenow={summary.completed}
                            aria-valuemin="0"
                            aria-valuemax={summary.total}
                          ></div>
                        </div>

                        {/* Stats Badges */}
                        <div className="d-flex gap-2 flex-wrap">
                          <span className="badge bg-light text-dark border">
                            <i className="bx bx-list-ul me-1"></i> Total: {summary.total}
                          </span>
                          <span className="badge bg-success-subtle text-success border border-success-subtle">
                            <i className="bx bx-check-circle me-1"></i> Completed: {summary.completed}
                          </span>
                          {summary.inProgress > 0 && (
                            <span className="badge bg-info-subtle text-info border border-info-subtle">
                              <i className="bx bx-loader me-1"></i> In Progress: {summary.inProgress}
                            </span>
                          )}
                          {summary.pendingReview > 0 && (
                            <span className="badge bg-warning-subtle text-warning border border-warning-subtle">
                              <i className="bx bx-time me-1"></i> Pending: {summary.pendingReview}
                            </span>
                          )}
                          {summary.expired > 0 && (
                            <span className="badge bg-danger-subtle text-danger border border-danger-subtle">
                              <i className="bx bx-error me-1"></i> Expired / Renew: {summary.expired}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="card-body">
                        {teacher.certifications.length === 0 ? (
                          <p className="text-muted mb-0">
                            No certifications recorded for this teacher yet.
                          </p>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-striped align-middle mb-0">
                              <thead>
                                <tr>
                                  <th>Course</th>
                                  <th>Status</th>
                                  <th>Completed</th>
                                  <th>Due by</th>
                                  <th>Renew by</th>
                                  <th>Hours</th>
                                  <th>Certificate</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sortedGroupNames.map((groupName) => {
                                  const group = groupedCerts[groupName];
                                  if (!group || group.certifications.length === 0) return null;

                                  return (
                                    <React.Fragment key={groupName}>
                                      {/* Group Header Row */}
                                      <tr className="table-group-header" style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                                        <td colSpan={8} style={{ padding: '12px 16px', fontSize: '1.1rem', borderTop: '2px solid #dee2e6' }}>
                                          {groupName}
                                        </td>
                                      </tr>
                                      {/* Group Certifications */}
                                      {group.certifications.map((cert) => {
                                        const course = courses.find(
                                          (item) => (item.key || item.courseKey) === cert.courseKey
                                        );
                                        const label = getCourseLabel(course, cert);
                                        const statusLabel =
                                          statusLabels[cert.status] ||
                                          cert.status ||
                                          "Not Started";
                                        const isCompleted = ["completed", "approved"].includes(
                                          (cert.status || "").toLowerCase()
                                        );
                                        const certIdentifier = cert._id || cert.id || `${cert.courseKey || "record"} - certificate`;
                                        const isDownloading = downloadingIds.has(certIdentifier);

                                        const isDueOverdue = isOverdue(cert.dueByDate, isCompleted);
                                        const isRenewExpired = isExpired(cert.renewByDate);

                                        return (
                                          <tr key={`${cert.courseKey} - ${cert._id || "record"}`}>
                                            <td>
                                              <div className="d-flex align-items-center">
                                                {isCompleted && (
                                                  <i
                                                    className="bx bx-check-circle text-success me-2"
                                                    style={{ fontSize: "1.2rem" }}
                                                    title="Certified"
                                                  />
                                                )}
                                                {label}
                                              </div>
                                            </td>
                                            <td>
                                              <span className={getStatusBadgeClass(cert.status)}>
                                                {statusLabel}
                                              </span>
                                            </td>
                                            <td>{formatDateDisplay(cert.dateCompleted)}</td>
                                            <td className={isDueOverdue ? "text-danger fw-bold" : ""}>
                                              {cert.dueByDate ? (
                                                formatDateDisplay(cert.dueByDate)
                                              ) : (
                                                // No stored due date means no deadline. This used to render a
                                                // date invented from a hardcoded 2026 calendar, flagged red and
                                                // "Overdue (Default)" - a deadline no school admin had set.
                                                <span className="text-muted">&mdash;</span>
                                              )}
                                              {isDueOverdue && <i className="bx bx-error-circle ms-1" title="Overdue"></i>}
                                            </td>
                                            <td className={isRenewExpired ? "text-danger fw-bold" : ""}>
                                              {cert.renewByDate ? (
                                                formatDateDisplay(cert.renewByDate)
                                              ) : (
                                                <span className="text-muted">&mdash;</span>
                                              )}
                                              {isRenewExpired && <i className="bx bx-error ms-1" title="Expired/Renew Needed"></i>}
                                            </td>
                                            <td>
                                              {cert.hours !== undefined &&
                                                cert.hours !== null
                                                ? cert.hours
                                                : "—"}
                                            </td>
                                            <td>
                                              {cert.certificateUrl ? (
                                                <button
                                                  type="button"
                                                  className="btn btn-link btn-sm p-0"
                                                  onClick={() =>
                                                    handleDownloadCertificate(cert, teacher.teacherId, label)
                                                  }
                                                  disabled={isDownloading}
                                                >
                                                  {isDownloading ? "Downloading..." : "View"}
                                                </button>
                                              ) : (
                                                "—"
                                              )}
                                            </td>
                                            <td>
                                              <div className="d-flex align-items-center gap-2">
                                                <button
                                                  type="button"
                                                  className="btn btn-sm btn-outline-primary"
                                                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                                                  onClick={() => handleEditAssignment(cert, teacher.teacherId, teacher.teacherName)}
                                                  title="Update Dates & Notes"
                                                >
                                                  <i className="bx bx-edit"></i>
                                                </button>

                                                {!cert._id?.startsWith('virtual-') && (
                                                  <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                                                    onClick={() => handleDeleteAssignment(cert._id || cert.id, label, teacher.teacherName)}
                                                    title="Remove Assignment"
                                                  >
                                                    <i className="bx bx-trash"></i>
                                                  </button>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              } catch (error) {
                console.error("Error rendering teacher card:", error, teacher);
                return (
                  <div key={teacher.teacherId} className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <p className="text-danger">
                          Error loading teacher: {teacher.teacherName || "Unknown"}
                        </p>
                        <pre className="small">{error.message}</pre>
                      </div>
                    </div>
                  </div>
                );
              }
            })}
          </div>
        )}
      </div>




    </DashboardLayout >
  );
};

export default SchoolAdminTeacherCertificationsDashboard;
